import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Video, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { meetingService } from '../../services/meetingService';

interface RecordingPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId: string;
  companyName?: string;
  leadName?: string;
  meetingDate?: string;
}

const RecordingPlayerModal: React.FC<RecordingPlayerModalProps> = ({
  isOpen,
  onClose,
  meetingId,
  companyName,
  leadName,
  meetingDate,
}) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUrl = async () => {
    setLoading(true);
    setError(null);
    setVideoUrl(null);
    setAudioUrl(null);

    const result = await meetingService.getRecordingUrl(meetingId);
    if (result.success) {
      setVideoUrl(result.video_url || null);
      setAudioUrl(result.audio_url || null);
    } else {
      setError(result.error || 'Failed to load recording');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen && meetingId) {
      fetchUrl();
    }
    return () => {
      setVideoUrl(null);
      setAudioUrl(null);
      setError(null);
    };
  }, [isOpen, meetingId]);

  if (!isOpen) return null;

  const subtitle = companyName
    ? `${leadName || 'Meeting'}${companyName ? ` - ${companyName}` : ''}${meetingDate ? ` - ${meetingDate}` : ''}`
    : `Recording${meetingDate ? ` - ${meetingDate}` : ''}`;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <Video className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Meeting Recording</h3>
              <p className="text-xs text-gray-500 font-medium">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-900 flex items-center justify-center min-h-[400px]">
          {loading && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={36} className="text-[#05E0D1] animate-spin" />
              <p className="text-gray-400 text-sm font-medium">Loading recording...</p>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center gap-4 px-8">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertCircle size={28} className="text-red-400" />
              </div>
              <p className="text-gray-400 text-sm font-medium text-center">{error}</p>
              <button
                onClick={fetchUrl}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm font-semibold transition-all"
              >
                <RefreshCw size={14} />
                Retry
              </button>
            </div>
          )}

          {!loading && !error && videoUrl && (
            <video
              controls
              autoPlay={false}
              className="w-full max-h-[65vh] bg-black"
              src={videoUrl}
            >
              Your browser does not support video playback.
            </video>
          )}

          {!loading && !error && !videoUrl && audioUrl && (
            <div className="flex flex-col items-center gap-6 px-8">
              <div className="w-20 h-20 rounded-full bg-[#1875FF]/10 flex items-center justify-center">
                <Video size={32} className="text-[#1875FF]" />
              </div>
              <p className="text-gray-400 text-sm font-medium">Audio-only recording</p>
              <audio controls autoPlay={false} src={audioUrl} className="w-full max-w-md">
                Your browser does not support audio playback.
              </audio>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecordingPlayerModal;
